import { Extension } from "@tiptap/core";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import type { RefObject } from "react";
import { createSuggestionRenderer, slashRow } from "./suggestion-popup";
import { SLASH_ICONS } from "./slash-icons";
import type { SlashCommandId, SlashCommandItem, SlashContext } from "./types";

/**
 * The "/" block menu.
 *
 * It exists because both description fields render with `showToolbar={false}` —
 * without a trigger there is no route at all to a heading, a list, a code block
 * or a table in the two places this editor is used most.
 */
export const SlashCommand = Extension.create<{
  suggestion: Partial<SuggestionOptions>;
}>({
  name: "slashCommand",
  addOptions() {
    return {
      suggestion: { char: "/", startOfLine: false, allowSpaces: false },
    };
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      } as SuggestionOptions),
    ];
  },
});

export const SLASH_COMMANDS: readonly SlashCommandItem[] = [
  {
    id: "text",
    title: "Text",
    keywords: "paragraph body plain",
    iconSvg: SLASH_ICONS.text,
    group: "Basic",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setNode("paragraph").run(),
  },
  {
    id: "h1",
    title: "Heading 1",
    keywords: "title h1 heading",
    iconSvg: SLASH_ICONS.h1,
    group: "Basic",
    run: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 1 })
        .run(),
  },
  {
    id: "h2",
    title: "Heading 2",
    keywords: "title h2 heading subtitle",
    iconSvg: SLASH_ICONS.h2,
    group: "Basic",
    run: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 2 })
        .run(),
  },
  {
    id: "h3",
    title: "Heading 3",
    keywords: "title h3 heading subtitle",
    iconSvg: SLASH_ICONS.h3,
    group: "Basic",
    run: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 3 })
        .run(),
  },
  {
    id: "quote",
    title: "Quote",
    keywords: "blockquote citation",
    iconSvg: SLASH_ICONS.quote,
    group: "Basic",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
  },
  {
    id: "codeBlock",
    title: "Code block",
    keywords: "code snippet pre syntax",
    iconSvg: SLASH_ICONS.codeBlock,
    group: "Basic",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    id: "divider",
    title: "Divider",
    keywords: "hr rule separator line",
    iconSvg: SLASH_ICONS.divider,
    group: "Basic",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
  {
    id: "bulletList",
    title: "Bulleted list",
    keywords: "ul unordered bullet",
    iconSvg: SLASH_ICONS.bulletList,
    group: "Lists",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    id: "orderedList",
    title: "Numbered list",
    keywords: "ol ordered number",
    iconSvg: SLASH_ICONS.orderedList,
    group: "Lists",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
  {
    id: "taskList",
    title: "To-do list",
    keywords: "checkbox checklist todo task",
    iconSvg: SLASH_ICONS.taskList,
    group: "Lists",
    run: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleTaskList().run(),
  },
  {
    id: "table",
    title: "Table",
    keywords: "grid rows columns",
    iconSvg: SLASH_ICONS.table,
    group: "Insert",
    run: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
  {
    id: "image",
    title: "Image",
    keywords: "picture photo upload",
    iconSvg: SLASH_ICONS.image,
    group: "Insert",
    /* The picker is async and opens outside the editor, so the trigger text has
     * to be removed first — otherwise the "/image" stays behind while the OS
     * file dialog is up. */
    run: ({ editor, range, ctx }) => {
      editor.chain().focus().deleteRange(range).run();
      ctx.pickImage();
    },
    isAvailable: (ctx) => ctx.canUploadImage,
  },
];

/** For call-site discoverability when passing `disabledSlashCommands`. */
export const SLASH_COMMAND_IDS: readonly SlashCommandId[] = SLASH_COMMANDS.map(
  (c) => c.id,
);

function filterSlashItems(
  query: string,
  ctx: SlashContext,
): SlashCommandItem[] {
  const parts = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return SLASH_COMMANDS.filter((item) => {
    if (ctx.disabled.includes(item.id)) return false;
    if (item.isAvailable && !item.isAvailable(ctx)) return false;
    if (!parts.length) return true;
    const haystack = `${item.title} ${item.keywords}`.toLowerCase();
    return parts.every((p) => haystack.includes(p));
  });
}

export function createSlashCommand(ctxRef: RefObject<SlashContext>) {
  return SlashCommand.configure({
    suggestion: {
      char: "/",
      startOfLine: false,
      allowSpaces: false,
      /* Same capture-once discipline as `mentionSuggestionsRef`: this closure
       * is baked into the editor on mount, so a direct capture would freeze
       * `canUploadImage: false` from the first render forever. */
      items: ({ query }: { query: string }) =>
        filterSlashItems(query, ctxRef.current),
      allow: ({ state, range }) => {
        // A "/" inside a code block is code, not a command.
        const $from = state.doc.resolve(range.from);
        return $from.parent.type.name !== "codeBlock";
      },
      command: ({ editor, range, props }) => {
        (props as SlashCommandItem).run({
          editor,
          range,
          ctx: ctxRef.current,
        });
      },
      render: createSuggestionRenderer<SlashCommandItem>({
        renderRow: (item) => slashRow(item),
        renderGroupHeading: (item, prev) =>
          prev?.group === item.group ? null : item.group,
        emptyText: "No blocks",
        width: 260,
        ariaLabel: "Insert a block",
      }),
    },
  });
}
